/**
 * Geometry Tools Tester Component
 * Interactive testing interface for GeometryTools methods
 * Allows users to test spatial analysis functions and see visual results
 */

import React, { useState, useEffect } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { GeometryTools } from '@/services/core/geometryTools';
import { ViewerService } from '@/services/core/viewerTools';
import { PropertiesService } from '@/services/core/propertiesTools';
import { toast } from '@/components/common/Toast';

// THREE is available globally when Forge Viewer is loaded
declare const THREE: any;

interface GeometryToolsTesterProps {
  elementDbId: number;
}

type TestMode = 'distance' | 'boundingBox' | 'spatial' | 'path' | 'utilities';
type DistanceMode = 'center-to-center' | 'surface-to-surface' | 'center-to-surface';

export function GeometryToolsTester({ elementDbId }: GeometryToolsTesterProps) {
  const { viewer } = useViewerStore();
  const [activeMode, setActiveMode] = useState<TestMode>('distance');
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Distance measurement state
  const [distanceMode, setDistanceMode] = useState<DistanceMode>('center-to-center');
  const [secondDbId, setSecondDbId] = useState<string>('');
  const [secondIdType, setSecondIdType] = useState<'dbId' | 'revitId'>('dbId');
  const [distanceResult, setDistanceResult] = useState<any>(null);
  const [isPickingSecond, setIsPickingSecond] = useState(false);
  
  // Bounding box state
  const [boxExtension, setBoxExtension] = useState(1.0);
  const [intersectingElements, setIntersectingElements] = useState<Array<{dbId: number; name: string; distance: number}>>([]);
  const [excludedElements, setExcludedElements] = useState<Array<{dbId: number; name: string; reason: string; distance?: number}>>([]);
  const [showBoundingBox, setShowBoundingBox] = useState(false);
  const [showExtendedBox, setShowExtendedBox] = useState(false);
  
  // Family filtering state
  const [useFamilyFilter, setUseFamilyFilter] = useState(false);
  const [availableFamilies, setAvailableFamilies] = useState<Array<{name: string; count: number}>>([]);
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [isLoadingFamilies, setIsLoadingFamilies] = useState(false);
  const [familySearchTerm, setFamilySearchTerm] = useState('');
  
  // Spatial queries state
  const [searchRadius, setSearchRadius] = useState(5.0);
  const [elementsInRadius, setElementsInRadius] = useState<Array<{dbId: number; distance: number}>>([]);
  const [relationshipTarget, setRelationshipTarget] = useState<number | null>(null);
  const [relationship, setRelationship] = useState<any>(null);
  const [useSpatialFamilyFilter, setUseSpatialFamilyFilter] = useState(false);
  const [selectedSpatialFamilies, setSelectedSpatialFamilies] = useState<string[]>([]);
  
  // Path analysis state
  const [pathStart, setPathStart] = useState<THREE.Vector3 | null>(null);
  const [pathEnd, setPathEnd] = useState<THREE.Vector3 | null>(null);
  const [pathWidth, setPathWidth] = useState(1.0);
  const [pathHeight, setPathHeight] = useState(2.0);
  const [pathAnalysis, setPathAnalysis] = useState<any>(null);
  
  // Utilities state
  const [volume, setVolume] = useState<number | null>(null);
  const [surfaceArea, setSurfaceArea] = useState<number | null>(null);
  const [levelMin, setLevelMin] = useState(0);
  const [levelMax, setLevelMax] = useState(3);
  const [isOnLevel, setIsOnLevel] = useState<boolean | null>(null);
  
  // Visual overlays management
  const [overlays, setOverlays] = useState<THREE.Object3D[]>([]);

  // Clean up overlays when component unmounts or selection changes
  useEffect(() => {
    // Highlight the source element when component mounts or element changes
    if (viewer && elementDbId) {
      // Clear previous highlights
      viewer.clearThemingColors();
      // Highlight source element in blue
      ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.3));
    }
    
    return () => {
      clearAllVisualizations();
      // Clear highlighting when unmounting
      if (viewer) {
        viewer.clearThemingColors();
      }
    };
  }, [elementDbId, viewer]);

  // Handle viewer selection for picking second element
  useEffect(() => {
    if (!viewer || !isPickingSecond) return;

    const handleSelection = (event: any) => {
      try {
        
        // Get selected items from the event
        const dbIds = event.dbIdArray || [];
        
        // If we have a selection and it's not the current element
        if (dbIds.length > 0) {
          const selectedId = dbIds[0];
          
          if (selectedId && selectedId !== elementDbId) {
            setSecondDbId(String(selectedId));
            setSecondIdType('dbId'); // Set to dbId since we're picking
            setIsPickingSecond(false);
            toast.success(`Selected element ${selectedId}`);
            
            // Highlight both elements with different colors
            ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.5)); // Blue for source
            ViewerService.highlightElement(viewer, selectedId, new THREE.Vector4(0, 1, 0, 0.5)); // Green for target
          } else if (selectedId === elementDbId) {
            toast.warning('Please select a different element');
          }
        }
      } catch (error) {
        toast.error('Error selecting element. Please try again.');
        setIsPickingSecond(false);
      }
    };

    // Use AGGREGATE_SELECTION_CHANGED_EVENT for more reliable selection handling
    const eventType = Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT || 
                     Autodesk.Viewing.SELECTION_CHANGED_EVENT;
    
    // Wrap event listener setup in try-catch to prevent errors
    try {
      viewer.addEventListener(eventType, handleSelection);
      
      // Optional: Disable other interactions temporarily
      if (viewer.navigation) {
        // Store current navigation mode
        const navMode = viewer.navigation.getRequestHomeView();
      }
    } catch (error) {
      setIsPickingSecond(false);
      toast.error('Selection mode not available. Please enter ID manually.');
    }
    
    return () => {
      try {
        viewer.removeEventListener(eventType, handleSelection);
      } catch (error) {
      }
    };
  }, [viewer, isPickingSecond, elementDbId]);

  // Clear all visual overlays
  const clearAllVisualizations = () => {
    if (!viewer) return;
    
    overlays.forEach(overlay => {
      viewer.impl.scene.remove(overlay);
    });
    setOverlays([]);
    viewer.impl.invalidate(true, true, true);
  };

  // Add visual overlay to viewer
  const addOverlay = (object: THREE.Object3D) => {
    if (!viewer) return;
    
    viewer.impl.scene.add(object);
    viewer.impl.invalidate(true, true, true);
    setOverlays(prev => [...prev, object]);
  };

  // Create line between two points
  const createLine = (point1: THREE.Vector3, point2: THREE.Vector3, color: number = 0x3B82F6) => {
    const geometry = new THREE.BufferGeometry().setFromPoints([point1, point2]);
    const material = new THREE.LineBasicMaterial({ color, linewidth: 3 });
    return new THREE.Line(geometry, material);
  };

  // Create box wireframe (custom implementation since Box3Helper may not be available)
  const createBoxWireframe = (box: THREE.Box3, color: number = 0x10B981) => {
    
    // Get box corners
    const min = box.min;
    const max = box.max;
    
    // Define the 8 corners of the box
    const corners = [
      new THREE.Vector3(min.x, min.y, min.z),
      new THREE.Vector3(max.x, min.y, min.z),
      new THREE.Vector3(max.x, max.y, min.z),
      new THREE.Vector3(min.x, max.y, min.z),
      new THREE.Vector3(min.x, min.y, max.z),
      new THREE.Vector3(max.x, min.y, max.z),
      new THREE.Vector3(max.x, max.y, max.z),
      new THREE.Vector3(min.x, max.y, max.z)
    ];
    
    // Define the 12 edges of the box
    const edges = [
      [0, 1], [1, 2], [2, 3], [3, 0], // Bottom face
      [4, 5], [5, 6], [6, 7], [7, 4], // Top face
      [0, 4], [1, 5], [2, 6], [3, 7]  // Vertical edges
    ];
    
    // Create line segments for each edge
    const geometry = new THREE.BufferGeometry();
    const positions = [];
    
    edges.forEach(edge => {
      positions.push(
        corners[edge[0]].x, corners[edge[0]].y, corners[edge[0]].z,
        corners[edge[1]].x, corners[edge[1]].y, corners[edge[1]].z
      );
    });
    
    // Use appropriate buffer attribute based on THREE.js version
    if (THREE.Float32BufferAttribute) {
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    } else {
      // Fallback for older THREE.js versions
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    }
    
    const material = new THREE.LineBasicMaterial({ 
      color: color, 
      linewidth: 2,
      depthTest: false,
      transparent: true,
      opacity: 0.8
    });
    
    return new THREE.LineSegments(geometry, material);
  };

  // Create sphere for radius visualization
  const createSphere = (center: THREE.Vector3, radius: number, color: number = 0x8B5CF6) => {
    const geometry = new THREE.SphereGeometry(radius, 32, 16);
    const material = new THREE.MeshBasicMaterial({ 
      color, 
      transparent: true, 
      opacity: 0.2,
      side: THREE.DoubleSide
    });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(center);
    return sphere;
  };

  // Convert meters to feet
  const metersToFeet = (meters: number): number => {
    return meters * 3.28084;
  };

  // Format distance for display
  const formatDistance = (meters: number): string => {
    const feet = metersToFeet(meters);
    return `${feet.toFixed(2)} ft (${meters.toFixed(2)} m)`;
  };

  // Helper function to get element name
  const getElementName = (dbId: number): Promise<string> => {
    return new Promise((resolve) => {
      if (!viewer || !viewer.model) {
        resolve(`Element_${dbId}`);
        return;
      }
      
      viewer.getProperties(dbId, (result) => {
        if (result && result.name) {
          resolve(result.name);
        } else {
          resolve(`Element_${dbId}`);
        }
      }, () => resolve(`Element_${dbId}`));
    });
  };

  // Parse family name from element name (removing bracketed ID)
  const parseFamilyName = (elementName: string): string => {
    // Check if there's a bracketed ID at the end
    const bracketMatch = elementName.match(/^(.+?)\s*\[\d+\]$/);
    
    if (bracketMatch) {
      // Return the part before the brackets, trimmed
      return bracketMatch[1].trim();
    }
    
    // If no brackets, return the entire name trimmed
    return elementName.trim();
  };

  // Load available families from the model
  const loadAvailableFamilies = async () => {
    if (!viewer || !viewer.model) {
      toast.warning('Model not loaded');
      return;
    }

    setIsLoadingFamilies(true);
    
    try {
      const tree = viewer.model.getInstanceTree();
      if (!tree) {
        toast.error('Could not access model tree');
        return;
      }

      const familyMap = new Map<string, number>();
      const allDbIds: number[] = [];
      
      // Collect all element IDs
      tree.enumNodeChildren(tree.getRootId(), (childId) => {
        allDbIds.push(childId);
      }, true);

      // Process elements to extract families
      for (const dbId of allDbIds) {
        const name = await getElementName(dbId);
        const familyName = parseFamilyName(name);
        
        // Skip non-physical elements
        const exclusionCheck = shouldExcludeElement(name, '');
        if (!exclusionCheck.exclude) {
          familyMap.set(familyName, (familyMap.get(familyName) || 0) + 1);
        }
      }

      // Convert to array and sort by count
      const families = Array.from(familyMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      setAvailableFamilies(families);
      toast.success(`Loaded ${families.length} families`);
    } catch (error) {
      toast.error('Failed to load families');
    } finally {
      setIsLoadingFamilies(false);
    }
  };

  // Visualize all valid (non-excluded) elements
  const visualizeValidElements = () => {
    if (!viewer || intersectingElements.length === 0) {
      toast.warning('No valid elements to visualize. Run "Find Intersecting" first.');
      return;
    }
    
    clearAllVisualizations();
    
    // Highlight source element in blue
    ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.6)); // Blue
    
    // Highlight all valid intersecting elements in green
    const validDbIds = intersectingElements.map(e => e.dbId);
    if (validDbIds.length > 0) {
      ViewerService.highlightElements(viewer, validDbIds, new THREE.Vector4(0, 1, 0, 0.5)); // Green
      
      // Fit view to show all elements
      const allDbIds = [elementDbId, ...validDbIds];
      viewer.fitToView(allDbIds, viewer.model, true);
      
      toast.success(`Visualizing ${validDbIds.length} valid elements (source in blue, valid in green)`);
    }
  };

  // View and highlight individual element
  const viewIndividualElement = (targetDbId: number, targetName: string) => {
    if (!viewer) return;
    
    // Clear previous highlights
    viewer.clearThemingColors();
    clearAllVisualizations();
    
    // Highlight source element in blue
    ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.4)); // Blue (lighter)
    
    // Highlight target element in bright green
    ViewerService.highlightElement(viewer, targetDbId, new THREE.Vector4(0, 1, 0, 0.8)); // Bright green
    
    // Fit view to show both elements
    viewer.fitToView([elementDbId, targetDbId], viewer.model, true);
    
    // Show connection line between elements
    setTimeout(() => {
      const sourceBox = GeometryTools.getElementBoundingBox(viewer, elementDbId);
      const targetBox = GeometryTools.getElementBoundingBox(viewer, targetDbId);
      
      if (sourceBox && targetBox) {
        const sourceCenter = sourceBox.getCenter(new THREE.Vector3());
        const targetCenter = targetBox.getCenter(new THREE.Vector3());
        
        // Create connection line
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array([
          sourceCenter.x, sourceCenter.y, sourceCenter.z,
          targetCenter.x, targetCenter.y, targetCenter.z
        ]);
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        const material = new THREE.LineBasicMaterial({ 
          color: 0xFFFF00, // Yellow
          linewidth: 2,
          depthTest: false,
          transparent: true,
          opacity: 0.6
        });
        
        const line = new THREE.Line(geometry, material);
        addOverlay(line);
      }
    }, 500); // Delay to ensure view has updated
    
    toast.info(`Viewing: ${targetName || `Element ${targetDbId}`}`);
  };

  // Distance Measurement Methods
  const measureDistance = async () => {
    
    if (!viewer || !secondDbId.trim()) {
      toast.warning('Please enter a valid second element ID');
      return;
    }

    clearAllVisualizations();
    
    try {
      let actualSecondDbId: number;
      
      // Convert Revit ID to DbID if needed
      if (secondIdType === 'revitId') {
        const foundDbId = await PropertiesService.findElementByRevitId(viewer, secondDbId.trim());
        if (foundDbId === null) {
          toast.error(`No element found with Revit ID: ${secondDbId}`);
          return;
        }
        actualSecondDbId = foundDbId;
      } else {
        actualSecondDbId = parseInt(secondDbId);
        if (isNaN(actualSecondDbId)) {
          toast.warning('Please enter a valid numeric DbID');
          return;
        }
      }
      
      const result = GeometryTools.measureDistance(viewer, elementDbId, actualSecondDbId, distanceMode);
      
      if (result) {
        setDistanceResult(result);
        
        // Clear any existing selection first
        viewer.clearSelection();
        
        // Select both elements to ensure they're visible
        viewer.select([elementDbId, actualSecondDbId]);
        
        // Fit view to show both elements with some padding
        viewer.fitToView([elementDbId, actualSecondDbId], viewer.model, true);
        
        // Wait for the camera animation to complete before highlighting
        setTimeout(() => {
          // Apply custom highlighting with different colors
          ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.6)); // Blue for source
          ViewerService.highlightElement(viewer, actualSecondDbId, new THREE.Vector4(0, 1, 0, 0.6)); // Green for target
          
          // Ensure viewer updates
          viewer.impl.invalidate(true, true, true);
        }, 500);
        
        // Visualize the measurement
        const line = createLine(result.point1, result.point2);
        addOverlay(line);
        
        // Add spheres at measurement points with size based on distance
        const sphereSize = Math.min(result.distance * 0.02, 0.5); // Scale sphere size
        const sphere1 = createSphere(result.point1, sphereSize, 0x0000FF); // Blue for source point
        const sphere2 = createSphere(result.point2, sphereSize, 0x00FF00); // Green for target point
        addOverlay(sphere1);
        addOverlay(sphere2);
        
        // Show bounding boxes for different measurement modes
        const box1 = GeometryTools.getElementBoundingBox(viewer, elementDbId);
        const box2 = GeometryTools.getElementBoundingBox(viewer, actualSecondDbId);
        
        if (box1 && box2) {
          if (distanceMode === 'surface-to-surface') {
            // Show both bounding boxes for surface-to-surface
            const wireframe1 = createBoxWireframe(box1, 0x0000FF); // Blue for source
            const wireframe2 = createBoxWireframe(box2, 0x00FF00); // Green for target
            addOverlay(wireframe1);
            addOverlay(wireframe2);
          } else if (distanceMode === 'center-to-surface') {
            // For center-to-surface, show first element's center and second element's surface
            // Measures from center of first element (toilet) to surface of second element (wall)
            const wireframe2 = createBoxWireframe(box2, 0x00FF00); // Green for second element (wall - provides surface)
            addOverlay(wireframe2);
            
            // Add a larger sphere at first element's center to emphasize it's the center source
            const sourceCenter = new THREE.Vector3();
            box1.getCenter(sourceCenter);
            const centerSphere = createSphere(sourceCenter, sphereSize * 1.5, 0x0000FF);
            addOverlay(centerSphere);
            
            // Add visual line from center to the surface point
            const centerLine = createLine(sourceCenter, result.point2, 0xFFFF00); // Yellow line
            addOverlay(centerLine);
          } else if (distanceMode === 'center-to-center') {
            // For center-to-center, show center points prominently
            const center1 = new THREE.Vector3();
            const center2 = new THREE.Vector3();
            box1.getCenter(center1);
            box2.getCenter(center2);
            
            // Add larger spheres at centers
            const centerSphere1 = createSphere(center1, sphereSize * 1.5, 0x0000FF);
            const centerSphere2 = createSphere(center2, sphereSize * 1.5, 0x00FF00);
            addOverlay(centerSphere1);
            addOverlay(centerSphere2);
          }
        }
        
        // Get element names for better display
        const sourceElementName = await getElementName(elementDbId);
        const targetElementName = await getElementName(actualSecondDbId);
        
        toast.success(`Distance: ${formatDistance(result.distance)}\nFrom: ${sourceElementName}\nTo: ${targetElementName}`);
      } else {
        toast.error('Failed to measure distance. Check if both elements exist.');
      }
    } catch (error) {
      toast.error('Error measuring distance');
    }
  };

  // Download distance measurement data as JSON
  const downloadDistanceJSON = async () => {
    if (!viewer || !distanceResult || !secondDbId) {
      toast.warning('No distance measurement data to download');
      return;
    }
    
    try {
      // Get the second element ID (convert if needed)
      let actualSecondDbId: number;
      if (secondIdType === 'revitId') {
        const foundDbId = await PropertiesService.findElementByRevitId(viewer, secondDbId.trim());
        if (foundDbId === null) {
          toast.error('Cannot find second element');
          return;
        }
        actualSecondDbId = foundDbId;
      } else {
        actualSecondDbId = parseInt(secondDbId);
      }
      
      // Get element properties for both elements
      const sourceProps = await PropertiesService.getElementProperties(viewer, elementDbId);
      const targetProps = await PropertiesService.getElementProperties(viewer, actualSecondDbId);
      
      // Get element names
      const sourceElementName = await getElementName(elementDbId);
      const targetElementName = await getElementName(actualSecondDbId);
      
      // Create comprehensive measurement data
      const measurementData = {
        measurement: {
          distance: {
            meters: distanceResult.distance,
            feet: metersToFeet(distanceResult.distance),
            formatted: formatDistance(distanceResult.distance)
          },
          type: distanceResult.type,
          mode: distanceMode,
          points: {
            start: {
              x: distanceResult.point1.x,
              y: distanceResult.point1.y,
              z: distanceResult.point1.z
            },
            end: {
              x: distanceResult.point2.x,
              y: distanceResult.point2.y,
              z: distanceResult.point2.z
            }
          }
        },
        sourceElement: {
          dbId: elementDbId,
          name: sourceElementName,
          properties: sourceProps
        },
        targetElement: {
          dbId: actualSecondDbId,
          name: targetElementName,
          properties: targetProps
        },
        metadata: {
          timestamp: new Date().toISOString(),
          viewer: 'Autodesk Forge Viewer',
          measurementMethod: `GeometryTools.measureDistance (${distanceMode})`
        }
      };
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(measurementData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `distance_measurement_${elementDbId}_to_${actualSecondDbId}_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Distance measurement data downloaded');
    } catch (error) {
      toast.error('Error downloading distance data');
    }
  };

  const startPickingSecond = () => {
    if (!viewer) {
      toast.error('Viewer not ready');
      return;
    }
    
    
    try {
      // Clear current selection first
      viewer.clearSelection();
      
      // Ensure viewer is in selection mode
      if (viewer.toolController) {
        // Simply ensure we're in selection mode
        // Don't try to deactivate other tools as it may not be supported
        try {
          // Try to get the default selection tool
          const selectionTool = viewer.toolController.getTool('select');
          if (selectionTool) {
            viewer.toolController.setActiveTools([selectionTool]);
          }
        } catch (e) {
        }
      }
      
      // Set the flag to listen for selection
      setIsPickingSecond(true);
      toast.info('Click on another element in the viewer to select it');
      
      // Optional: Set selection mode to single object
      if (viewer.setSelectionMode) {
        viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.REGULAR);
      }
    } catch (error) {
      toast.error('Could not start selection mode. Please try manual ID entry.');
      setIsPickingSecond(false);
    }
  };

  // Bounding Box Methods
  const showElementBoundingBox = () => {
    if (!viewer) return;
    
    clearAllVisualizations();
    
    try {
      const box = GeometryTools.getElementBoundingBox(viewer, elementDbId);
      
      if (box) {
        const wireframe = createBoxWireframe(box);
        addOverlay(wireframe);
        setShowBoundingBox(true);
        
        const size = new THREE.Vector3();
        box.getSize(size);
        const sizeInFeet = {
          x: metersToFeet(size.x),
          y: metersToFeet(size.y),
          z: metersToFeet(size.z)
        };
        toast.info(`Box size: ${sizeInFeet.x.toFixed(2)} × ${sizeInFeet.y.toFixed(2)} × ${sizeInFeet.z.toFixed(2)} ft`);
      } else {
        toast.error('Could not get bounding box for element');
      }
    } catch (error) {
      toast.error('Error showing bounding box');
    }
  };

  const showExtendedBoundingBox = () => {
    if (!viewer) return;
    
    clearAllVisualizations();
    
    try {
      const box = GeometryTools.getElementBoundingBox(viewer, elementDbId);
      
      if (box) {
        // Show original box
        const originalWireframe = createBoxWireframe(box, 0x10B981);
        addOverlay(originalWireframe);
        
        // Show extended box
        const extended = GeometryTools.extendBoundingBox(box, boxExtension);
        const extendedWireframe = createBoxWireframe(extended.extended, 0xF59E0B);
        addOverlay(extendedWireframe);
        
        setShowExtendedBox(true);
        const extensionInFeet = metersToFeet(boxExtension);
        toast.info(`Extended by ${extensionInFeet.toFixed(2)} ft in all directions`);
      } else {
        toast.error('Could not get bounding box for element');
      }
    } catch (error) {
      toast.error('Error showing extended bounding box');
    }
  };

  // Download intersecting elements data as JSON
  const downloadIntersectionsJSON = async () => {
    if (!viewer || (intersectingElements.length === 0 && excludedElements.length === 0)) {
      toast.warning('No intersection data to download');
      return;
    }
    
    const intersectionData = {
      sourceElement: {
        dbId: elementDbId,
        name: await getElementName(elementDbId)
      },
      extension: {
        meters: boxExtension,
        feet: metersToFeet(boxExtension)
      },
      familyFilter: useFamilyFilter ? {
        enabled: true,
        selectedFamilies: selectedFamilies,
        availableFamilies: availableFamilies
      } : {
        enabled: false
      },
      validIntersectingElements: intersectingElements.map(el => ({
        dbId: el.dbId,
        name: el.name,
        family: parseFamilyName(el.name),
        distance: {
          meters: el.distance,
          feet: metersToFeet(el.distance)
        }
      })),
      excludedElements: excludedElements,
      filterCriteria: {
        abstractContainers: {
          description: 'Generic category names that represent a collection, not a single object',
          examples: ['Walls', 'Doors', 'Floors', 'Grids', 'Lines', 'Furniture', 'Plumbing Fixtures', 'Specialty Equipment', 'Windows', 'Columns', 'Railings', 'Stairs', 'Casework', 'Roofs', 'Ceilings', 'Structural Framing', 'Mechanical Equipment', 'Electrical Equipment', 'Lighting Fixtures'],
          matchType: 'Exact match (case-insensitive)'
        },
        siteAndLandscape: {
          description: 'Large-scale environmental objects',
          examples: ['Site', 'Grass', 'Parking Pavement', 'Sidewalk', 'Asphalt', 'Planting', 'Lawn', 'Landscape', 'Concrete Curb', 'Driveway', 'Walkway', 'Plaza', 'Courtyard', 'Terrace', 'Topography', 'Building Pad', 'Property Line', 'Hardscape', 'Softscape'],
          matchType: 'Exact match for single words, contains for multi-word phrases'
        },
        nonPhysicalAndReference: {
          description: 'All non-physical, abstract, or reference geometry',
          types: [
            'Any name containing "Grid"',
            '<Room Separation> (with or without brackets)',
            'Any name containing "Import Symbol"',
            'Files ending in .dwg',
            'Reference planes, scope boxes, section boxes',
            'Annotations, tags, dimensions, text',
            'Model lines, symbolic lines, detail lines',
            'Views, cameras, sections, elevations',
            'Schedules, legends, keynotes',
            'Voids, openings, shaft openings',
            'Worksets, phases, revision clouds'
          ],
          matchType: 'Contains match (case-insensitive)'
        },
        projectAndLevelIdentifiers: {
          description: 'Elements that define an entire level or the project itself',
          examples: ['Model', '1ST FLOOR', 'LEVEL 1', 'B1', 'GF', 'Names containing "Project"'],
          patterns: [
            'Exact "Model"',
            'Floor patterns: 1ST FLOOR, 2ND FLOOR, etc.',
            'Level patterns: LEVEL 1, LEVEL 2, etc.',
            'Basement/Ground: B1, B2, G, GF, P1, P2',
            'Any name containing "Project"',
            'Survey points, base points, project points'
          ],
          matchType: 'Exact match or pattern match'
        },
        systemElements: {
          description: 'System families and non-object elements',
          examples: ['System Family', 'System Type', 'Family Type', '.rfa files', 'Matchlines', 'Guides', 'Constraints'],
          matchType: 'Contains match'
        },
        nonArchitecturalElements: {
          description: 'Office furniture, kitchen appliances, and other non-architectural items',
          categories: {
            'Office/Kitchen': ['Microwave', 'Chair', 'Desk', 'Table', 'Monitor', 'Keyboard', 'Coffee Maker', 'Refrigerator'],
            'Window Treatments': ['Knockout', 'Shade', 'Window-Treatment', 'Blinds', 'Curtain', 'Solar Shade'],
            'Electrical/Data': ['Outlet', 'Receptacle', 'Switch', 'Data Port', 'Cable Tray', 'Conduit'],
            'HVAC/Mechanical': ['Diffuser', 'Grille', 'Vent', 'Thermostat', 'Sprinkler Head', 'Detector'],
            'Decorative': ['Artwork', 'Plant', 'Vase', 'Clock', 'Mirror Frame'],
            'Furniture Components': ['Armrest', 'Cushion', 'Drawer Pull', 'Table Leg', 'Glide'],
            'Technology': ['Projector', 'Screen', 'Speaker', 'Phone', 'Camera'],
            'Signage': ['Sign', 'Nameplate', 'Directory', 'Logo', 'Graphics']
          },
          totalKeywords: '150+ exclusion terms',
          matchType: 'Contains match (case-insensitive)'
        },
        genericDimensions: {
          description: 'Elements with only dimension-based or generic component names',
          patterns: [
            '"0.242 m x 0.098 m" - metric dimensions only',
            '"Lam 30D x 66W" - material with dimensions',
            '"24\\" Depth 2" - single dimension descriptors',
            '"20 1/2w X 12 1/8h X 16d" - fractional dimensions',
            '"36W Pwr/Data Knockouts" - dimensions with function',
            '"Matte Black" - generic finishes only',
            '"2H - 24D x 30W" - component dimensions',
            '"26.25H X 66W" - decimal dimensions',
            '"cm_147351494657" - generic codes',
            '"Fxd-Hgt M-4H Foot for Metal" - generic mounting components'
          ],
          matchType: 'Pattern match (regex)'
        }
      },
      summary: {
        validCount: intersectingElements.length,
        excludedCount: excludedElements.length,
        totalAnalyzed: intersectingElements.length + excludedElements.length
      },
      timestamp: new Date().toISOString()
    };
    
    // Create and download JSON file
    const blob = new Blob([JSON.stringify(intersectionData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `intersections_${elementDbId}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Intersection data downloaded');
  };

  // Helper function to check if element should be excluded
  // Per user instructions: "exclude any object that is not a specific, physical item relevant to a clash or proximity check"
  const shouldExcludeElement = (name: string, sourceElementName?: string): { exclude: boolean; reason: string } => {
    const lowerName = name.toLowerCase().trim();
    
    // Check if source element is a bathroom fixture (toilet, urinal, sink, shower)
    const isBathroomContext = sourceElementName && (
      sourceElementName.toLowerCase().includes('toilet') ||
      sourceElementName.toLowerCase().includes('urinal') ||
      sourceElementName.toLowerCase().includes('sink') ||
      sourceElementName.toLowerCase().includes('shower') ||
      sourceElementName.toLowerCase().includes('lavatory')
    );
    
    // 1. Abstract Containers (plural forms that represent collections, not individual items)
    // Only exclude EXACT matches for plural container names
    const abstractContainers = [
      'walls', 'doors', 'floors', 'windows', 'ceilings', 'roofs',
      'columns', 'beams', 'furniture', 'fixtures', 'equipment', 
      'pipes', 'ducts', 'conduits', 'cables', 'railings',
      'stairs', 'ramps', 'elevators', 'curtain panels', 'casework',
      'structural columns', 'structural framing', 'plumbing fixtures',
      'mechanical equipment', 'electrical equipment', 'electrical fixtures',
      'generic models', 'curtain systems', 'curtain wall mullions',
      'cable trays', 'specialty equipment', 'lines', 'grids',
      'lighting fixtures', 'communication devices', 'data devices',
      'fire alarm devices', 'security devices', 'nurse call devices',
      'furniture systems', 'runs', 'supports', 'handrails', 'top rails',
      'duct systems', 'pipe systems', 'structural foundations'
    ];
    
    // Check for exact match (case-insensitive)
    if (abstractContainers.includes(lowerName)) {
      return { exclude: true, reason: 'Abstract container (collection)' };
    }
    
    // 2. Site & Landscape Elements (large-scale environmental objects)
    const siteKeywords = [
      'site', 'grass', 'parking', 'pavement', 'sidewalk', 'asphalt',
      'planting', 'landscape', 'topography', 'terrain', 'ground',
      'soil', 'earth', 'vegetation', 'tree', 'shrub', 'lawn',
      'irrigation', 'drainage', 'building pad', 'property line',
      'concrete curb', 'driveway', 'walkway', 'plaza', 'courtyard',
      'terrace', 'hardscape', 'softscape'
    ];
    
    for (const keyword of siteKeywords) {
      if (lowerName.includes(keyword)) {
        return { exclude: true, reason: 'Site/landscape element' };
      }
    }
    
    // 3. Non-Physical & Reference Elements
    const nonPhysicalKeywords = [
      'grid', 'grids', 'room separation', 'room separator', 'import symbol',
      '.dwg', '.dxf', '.rfa', 'reference plane', 'ref plane', 'annotation', 
      'dimension', 'tag', 'symbol', 'legend', 'schedule', 'title block', 
      'sheet', 'viewport', 'matchline', 'match line', 'section line', 
      'elevation mark', 'detail mark', 'callout', 'keynote', 'text note', 
      'guide', 'constraint', 'zone', 'space', 'area', 'room tag',
      'scope box', 'section box', 'detail line', 'model line', 'symbolic line',
      'hidden line', 'axis', 'elevation', 'section', 'view', 'camera',
      'render', 'revision cloud', 'detail component', 'repeating detail',
      'filled region', 'masking region', 'opening', 'void', 'reference',
      'workset', 'phase', 'boundary', 'shaft'
    ];
    
    for (const keyword of nonPhysicalKeywords) {
      if (lowerName.includes(keyword)) {
        return { exclude: true, reason: 'Non-physical/reference element' };
      }
    }
    
    // Special case for angle brackets which often indicate system/category names
    if (lowerName.includes('<') || lowerName.includes('>')) {
      return { exclude: true, reason: 'System/category indicator' };
    }
    
    // 4. Project or Level Identifiers
    // Check for generic level/floor identifiers
    if (lowerName === 'model' ||
        lowerName === 'project' ||
        lowerName === 'system' ||
        lowerName.includes('project information') ||
        lowerName.includes('project base point') ||
        lowerName.includes('survey point') ||
        lowerName.includes('shared coordinates') ||
        lowerName.includes('project point')) {
      return { exclude: true, reason: 'Project identifier' };
    }
    
    // Check for level/floor patterns
    if (lowerName.match(/^(level|floor|story|storey)\s*\d*$/i) ||
        lowerName.match(/^\d+(st|nd|rd|th)\s*(floor|level|story|storey)$/i) ||
        lowerName.match(/^[lgbp]\d+$/i) || // L1, G1, B1, P1, etc.
        lowerName.match(/^gf$/i) || // GF
        lowerName.match(/^(basement|ground|first|second|third|fourth|fifth)\s*(floor|level)?$/i) ||
        lowerName.includes('level ') ||
        /\blevel\s+\d+/i.test(lowerName)) {
      return { exclude: true, reason: 'Level/floor identifier' };
    }
    
    // 5. System families and empty/null elements
    if (lowerName.includes('system family') ||
        lowerName.includes('system type') ||
        lowerName.includes('family type') ||
        lowerName.includes(' type') && !lowerName.includes('type ') ||
        lowerName === '' ||
        lowerName === 'null' ||
        lowerName === 'undefined') {
      return { exclude: true, reason: 'System/invalid element' };
    }
    
    // 6. CONTEXTUAL EXCLUSIONS FOR OFFICE/KITCHEN EQUIPMENT AND IRRELEVANT ITEMS
    // Always exclude office, kitchen equipment, and other non-architectural elements
    const exclusionKeywords = [
      // Office furniture and equipment
      'microwave', 'chair', 'seating', 'desk', 'worksurface', 'credenza',
      'table', 'monitor', 'keyboard', 'mouse', 'vase', 'coffee', 'pen',
      'hermanmiller', 'herman miller', 'steelcase', 'national', 'orangebox',
      'haworth', 'notepad', 'bookcase', 'file cabinet', 'lateral file',
      'storage cabinet', 'refrigerator', 'copier', 'printer', 'scanner',
      'whiteboard', 'tackboard', 'bulletin board', 'coffee maker', 'water cooler',
      'ice machine', 'dishwasher', 'task light', 'desk lamp', 'pencil',
      'stapler', 'paper tray', 'inbox', 'outbox', 'file folder', 'binder',
      'receptacle', 'power strip', 'surge protector', 'cpu holder', 'footrest',
      'ergonomic', 'lumbar', 'mesh back', 'casters', 'swivel', 'adjustable arm',
      'modesty panel', 'privacy screen', 'cubicle', 'workstation', 'partition panel',
      'systems furniture', 'modular furniture', 'task chair', 'guest chair',
      'conference chair', 'lounge chair', 'ottoman', 'side table', 'end table',
      'coffee table', 'console table', 'credenza', 'hutch', 'overhead storage',
      'pedestal', 'mobile pedestal', 'tower', 'wardrobe', 'coat rack',
      
      // Additional keywords as requested
      'knockout', 'knockouts', 'shade', 'shades', 'window-treatment', 'window treatment',
      'blinds', 'curtain', 'drape', 'valance', 'roller shade', 'solar shade',
      'venetian', 'vertical blind', 'horizontal blind', 'mini blind',
      
      // Electrical and data components (often not relevant for spatial analysis)
      'outlet', 'receptacle', 'switch', 'dimmer', 'junction box', 'electrical box',
      'data port', 'network port', 'ethernet', 'cable tray', 'wire mold',
      'raceway', 'conduit', 'circuit breaker', 'panel board', 'transformer',
      
      // HVAC and mechanical components (unless specifically needed)
      'diffuser', 'grille', 'register', 'vent', 'louver', 'damper',
      'thermostat', 'sensor', 'detector', 'sprinkler head', 'smoke detector',
      'motion sensor', 'occupancy sensor', 'exit sign', 'emergency light',
      
      // Decorative and non-structural items
      'artwork', 'painting', 'picture', 'poster', 'frame', 'mirror frame',
      'plant', 'planter', 'pot', 'vase', 'sculpture', 'decoration',
      'ornament', 'trophy', 'award', 'clock', 'wall clock', 'calendar',
      
      // Furniture accessories and small items
      'cushion', 'pillow', 'throw', 'blanket', 'rug', 'mat', 'carpet tile',
      'drawer pull', 'handle', 'knob', 'hinge', 'latch', 'lock',
      'paper holder', 'tissue holder', 'napkin dispenser', 'cup dispenser',
      
      // Generic furniture components
      'arm rest', 'armrest', 'head rest', 'headrest', 'back support',
      'seat cushion', 'seat pan', 'table leg', 'table base', 'pedestal base',
      'glide', 'leveler', 'spacer', 'filler', 'trim piece', 'edge band',
      
      // Storage and organizational items
      'bin', 'basket', 'tray', 'organizer', 'divider', 'separator',
      'shelf pin', 'shelf support', 'bracket', 'file hanger', 'coat hook',
      
      // Technology and AV equipment
      'projector', 'screen', 'projection screen', 'tv mount', 'monitor arm',
      'speaker', 'microphone', 'camera', 'webcam', 'phone', 'telephone',
      'intercom', 'paging', 'sound masking', 'white noise',
      
      // Signage and graphics
      'sign', 'signage', 'nameplate', 'label', 'directory', 'wayfinding',
      'logo', 'branding', 'graphic', 'decal', 'sticker', 'vinyl lettering'
    ];
    
    for (const keyword of exclusionKeywords) {
      if (lowerName.includes(keyword)) {
        return { exclude: true, reason: 'Non-architectural/irrelevant element' };
      }
    }
    
    // 7. GENERIC DIMENSION-ONLY NAMES - ENHANCED
    // Exclude items that are only dimensions or generic size descriptions
    const dimensionPatterns = [
      // Pure dimension patterns (most common)
      /^\d+\.?\d*\s*[mx]\s*\d+\.?\d*\s*[mx]?(\s*\d+\.?\d*)?$/i, // "0.242 m x 0.098 m"
      /^\d+\.?\d*\s*x\s*\d+\.?\d*(\s*x\s*\d+\.?\d*)?$/i, // "24 x 36" or "12 x 24 x 6"
      /^[\d\s\/\-\.]+["']?\s*x\s*[\d\s\/\-\.]+["']?(\s*x\s*[\d\s\/\-\.]+["']?)?$/i, // "20 1/2" x 12 1/8" x 16"
      /^[\d\s\/\-\.]+["']?$/i, // Just a number like "18'" or "24.5"
      
      // Dimension with suffix patterns
      /^\d+\.?\d*[hwdlxt]\s*x\s*\d+\.?\d*[hwdlxt](\s*x\s*\d+\.?\d*[hwdlxt])?$/i, // "20w X 12h X 16d"
      /^[\d\s\/]+[wx]["']?\s*[xX]\s*[\d\s\/]+[hd]["']?(\s*[xX]\s*[\d\s\/]+[dwlht]["']?)?$/i, // Various dimension formats
      /^[\d\s.]+[h]\s*[xX]\s*[\d\s.]+[w]$/i, // "26.25H X 66W"
      /^\d+[h]\s*[-–]\s*\d+[dwlh]\s*[x]\s*\d+[dwlh]$/i, // "2H - 24D x 30W"
      
      // Material/Type with dimensions
      /^lam\s+\d+[dwh]\s*x\s*\d+[dwh]$/i, // "Lam 30D x 66W"
      /^(lam|laminate|wood|metal|plastic|vinyl)\s+[\d\s\/]+\s*x\s*[\d\s\/]+/i, // Material + dimensions
      
      // Dimension descriptors
      /^\d+["']?\s*(depth|width|height|deep|wide|high|tall|thick)\s*\d*$/i, // "24" Depth 2"
      /^\d+\s*(inch|inches|foot|feet|mm|cm|meter|meters)\s*(depth|width|height)?$/i, // "24 inches depth"
      
      // Component/Part patterns
      /^\d+[wx]\s+(pwr|data|power|knockouts?|outlet|receptacle|port)$/i, // "36W Pwr/Data Knockouts"
      /^(pwr|power|data|electrical|network)\s*\/?\s*(data|outlet|knockouts?|port)?$/i, // "Pwr/Data"
      /^\d+\s*(amp|volt|watts?|phase)$/i, // Electrical specifications
      
      // Generic finishes and materials (standalone)
      /^(matte|gloss|satin|chrome|brushed|polished|painted|powder[\s-]?coated?)\s*(black|white|silver|gray|grey|steel|aluminum|bronze|brass|copper|gold)?$/i,
      /^(canvas|mesh|fabric|leather|vinyl|plastic|metal|wood|laminate|glass|ceramic|porcelain)\s*(full|half|partial|finished|unfinished)?\s*(lam|laminate|case|top|surface|panel)?$/i,
      
      // Generic descriptors
      /^(fixed|adj|adjustable|standard|custom|modular|universal)[\s-]*(height|hgt|width|depth|size|dimension)?\s*(range|type|style)?$/i,
      /^(left|right|center|top|bottom|upper|lower|middle)\s*(hand|side|mount|position)?$/i,
      /^(single|double|triple|quad)\s*(door|drawer|shelf|panel)?$/i,
      
      // Generic codes and IDs
      /^cm_\d+$/i, // "cm_147351494657"
      /^[a-z]{1,3}[-_]\d{4,}$/i, // Generic codes like "ab_12345"
      /^(type|model|series|style)\s*\d+[a-z]?$/i, // "Type 00", "Model 5A"
      
      // Mounting and support components
      /^(fxd|fixed|adj|adjustable)[-\s]*(hgt|height|mount|base|support|bracket|foot|leg|arm|post)\s*[a-z0-9-]*\s*(for\s+\w+)?$/i,
      /^(mounting|support|bracket|hanger|clip|clamp|fastener|connector)\s*(type|style)?\s*[a-z0-9-]*$/i,
      
      // Simple letters/numbers combinations
      /^[a-z]\d+[a-z]?$/i, // "B4221", "M4H"
      /^\d+[a-z]{1,2}$/i, // "24D", "30W"
      
      // Fractional dimensions
      /^[\d\s]+[\/\-][\d\s]+["']?(\s*[xX]\s*[\d\s]+[\/\-][\d\s]+["']?)?$/i, // "20 1/2" or "20 1/2 x 12 1/8"
      
      // Dimensions with units
      /^\d+\.?\d*\s*(mm|cm|m|in|ft|'|")\s*[xX]\s*\d+\.?\d*\s*(mm|cm|m|in|ft|'|")(\s*[xX]\s*\d+\.?\d*\s*(mm|cm|m|in|ft|'|"))?$/i,
      
      // Component descriptions without specific names
      /^(component|part|element|piece|unit|item|assembly)\s*\d*$/i,
      
      // Generic panel/board descriptions
      /^(panel|board|sheet|plate)\s*\d*\s*(type|style)?\s*[a-z0-9]*$/i
    ];
    
    for (const pattern of dimensionPatterns) {
      if (pattern.test(lowerName)) {
        return { exclude: true, reason: 'Generic dimension/component name' };
      }
    }
    
    // 8. BATHROOM CONTEXT SPECIFIC EXCLUSIONS
    // When checking toilet/bathroom fixtures, exclude unrelated elements
    if (isBathroomContext) {
      // Exclude outdoor elements
      if (lowerName.includes('fence') ||
          lowerName.includes('bollard') ||
          lowerName.includes('dumpster') ||
          lowerName.includes('waste container') ||
          lowerName.includes('trash gate') ||
          lowerName.includes('railing') ||
          lowerName.includes('taxi way') ||
          lowerName.includes('dry pond')) {
        return { exclude: true, reason: 'Outdoor element - not relevant to bathroom' };
      }
      
      // Exclude non-bathroom rooms
      if (lowerName === 'breakroom' ||
          lowerName.includes('import symbol')) {
        return { exclude: true, reason: 'Non-bathroom space' };
      }
      
      // Exclude stairs and columns when checking toilet
      if (lowerName.includes('stair') ||
          lowerName.includes('stringer') ||
          lowerName.includes('column') && !lowerName.includes('grab')) {
        return { exclude: true, reason: 'Structural element - not relevant to bathroom fixture' };
      }
      
      // Exclude curtain walls and windows (unless ADA compliant bathroom with window)
      if (lowerName.includes('curtain wall') ||
          lowerName.includes('window treatment') ||
          lowerName.includes('solar shade')) {
        return { exclude: true, reason: 'Window element - not directly relevant to bathroom fixture' };
      }
      
      // Exclude numbered elements (room numbers, generic labels)
      if (/^\d+$/.test(lowerName) || // Just numbers like "122", "2222"
          /^\d+\s*\(.*\)$/.test(lowerName) || // Numbers with descriptions
          /^\d+[wx]\d+/.test(lowerName)) { // Dimensions like "48W"
        return { exclude: true, reason: 'Generic numbering - not a physical element' };
      }
    }
    
    // Default: INCLUDE the element - it's a specific physical item
    return { exclude: false, reason: '' };
  };

  const findIntersectingElements = async () => {
    if (!viewer) return;
    
    clearAllVisualizations();
    
    try {
      // First show the extended box to visualize search area
      const sourceBox = GeometryTools.getElementBoundingBox(viewer, elementDbId);
      if (!sourceBox) {
        toast.error('Could not get bounding box for source element');
        return;
      }
      
      const extended = GeometryTools.extendBoundingBox(sourceBox, boxExtension);
      const extendedWireframe = createBoxWireframe(extended.extended, 0xF59E0B);
      addOverlay(extendedWireframe);
      
      const elements = GeometryTools.findIntersectingElements(viewer, elementDbId, boxExtension);
      
      // Get source element info
      const sourceElementName = await getElementName(elementDbId);
      const sourceCenter = sourceBox.getCenter(new THREE.Vector3());
      
      // Filter out the element itself and non-physical elements
      const validElements: Array<{dbId: number; name: string; distance: number}> = [];
      const excluded: Array<{dbId: number; name: string; reason: string; distance?: number}> = [];
      
      // Build families map if using family filter
      const familyMap = new Map<string, number>();
      if (useFamilyFilter && elements.length > 0) {
        for (const id of elements) {
          if (id === elementDbId) continue;
          const name = await getElementName(id);
          const familyName = parseFamilyName(name);
          familyMap.set(familyName, (familyMap.get(familyName) || 0) + 1);
        }
        
        // Update available families list
        const families = Array.from(familyMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAvailableFamilies(families);
        
      }
      
      for (const id of elements) {
        if (id === elementDbId) continue; // Skip self
        
        // Get element name and bounding box
        const name = await getElementName(id);
        const elementBox = GeometryTools.getElementBoundingBox(viewer, id);
        
        // Calculate CENTER-TO-SURFACE distance
        let distance = 0;
        if (elementBox) {
          // Use center-to-surface calculation for consistency
          const closestPoint = GeometryTools.getClosestPointOnBox(sourceCenter, elementBox);
          distance = sourceCenter.distanceTo(closestPoint);
        }
        
        // Apply family filter first if enabled
        if (useFamilyFilter && selectedFamilies.length > 0) {
          const familyName = parseFamilyName(name);
          if (!selectedFamilies.includes(familyName)) {
            excluded.push({ 
              dbId: id, 
              name, 
              reason: `Family not selected: ${familyName}`,
              distance: distance
            });
            continue; // Skip to next element
          }
        }
        
        // Exclude elements with 0 distance (overlapping/coincident elements)
        if (distance === 0) {
          excluded.push({ 
            dbId: id, 
            name, 
            reason: 'Zero distance - overlapping/coincident element',
            distance: 0
          });
        } else if (useFamilyFilter && selectedFamilies.length > 0) {
          // If using family filter and element passed, include it directly (skip other exclusions)
          validElements.push({ dbId: id, name, distance });
        } else {
          // Apply standard exclusion logic if not using family filter
          const exclusionCheck = shouldExcludeElement(name, sourceElementName);
          
          if (exclusionCheck.exclude) {
            excluded.push({ 
              dbId: id, 
              name, 
              reason: exclusionCheck.reason,
              distance: distance
            });
          } else {
            // This is a specific physical item - include it
            validElements.push({ dbId: id, name, distance });
          }
        }
      }
      
      // Sort valid elements by distance
      validElements.sort((a, b) => a.distance - b.distance);
      
      setIntersectingElements(validElements);
      setExcludedElements(excluded);
      
      
      // Highlight intersecting elements with distinct color
      if (validElements.length > 0) {
        // Highlight source element in blue
        ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.5));
        // Highlight valid intersecting elements in orange
        const dbIds = validElements.map(e => e.dbId);
        ViewerService.highlightElements(viewer, dbIds, new THREE.Vector4(1, 0.5, 0, 0.8));
        toast.success(`Found ${validElements.length} relevant elements (${excluded.length} excluded)`);
      } else {
        toast.info(`No relevant intersecting elements found (${excluded.length} excluded)`);
      }
    } catch (error) {
      toast.error('Error finding intersecting elements');
    }
  };

  // Spatial Query Methods
  const findElementsWithinRadius = async () => {
    if (!viewer) return;
    
    clearAllVisualizations();
    
    try {
      const results = GeometryTools.findElementsWithinRadius(viewer, elementDbId, searchRadius);
      
      // Get source element name for context-aware filtering
      const sourceElementName = await getElementName(elementDbId);
      
      // Filter results using the same exclusion logic
      const validResults: Array<{dbId: number; distance: number; name: string}> = [];
      const excluded: Array<{dbId: number; name: string; reason: string}> = [];
      
      for (const result of results) {
        const name = await getElementName(result.dbId);
        
        // Exclude elements with 0 distance (overlapping/coincident elements)
        if (result.distance === 0) {
          excluded.push({ 
            dbId: result.dbId, 
            name, 
            reason: 'Zero distance - overlapping/coincident element'
          });
        } else {
          // Apply family filter first if enabled
          if (useSpatialFamilyFilter && selectedSpatialFamilies.length > 0) {
            const familyName = parseFamilyName(name);
            if (!selectedSpatialFamilies.includes(familyName)) {
              excluded.push({ 
                dbId: result.dbId, 
                name, 
                reason: `Family not selected: ${familyName}`
              });
                continue;
            }
          }
          
          // Apply exclusion logic per user instructions (with bathroom context awareness)
          const exclusionCheck = shouldExcludeElement(name, sourceElementName);
          
          if (!exclusionCheck.exclude) {
            // This is a specific physical item - include it
            validResults.push({ ...result, name });
          } else {
            excluded.push({ 
              dbId: result.dbId, 
              name, 
              reason: exclusionCheck.reason
            });
          }
        }
      }
      
      setElementsInRadius(validResults);
      
      // Visualize search sphere
      const box = GeometryTools.getElementBoundingBox(viewer, elementDbId);
      if (box) {
        const center = box.getCenter(new THREE.Vector3());
        const sphere = createSphere(center, searchRadius);
        addOverlay(sphere);
      }
      
      // Highlight found elements
      if (validResults.length > 0) {
        const dbIds = validResults.map(r => r.dbId);
        ViewerService.highlightElements(viewer, dbIds, new THREE.Vector4(0.5, 0, 1, 0.5));
        const radiusInFeet = metersToFeet(searchRadius);
        toast.success(`Found ${validResults.length} relevant elements within ${radiusInFeet.toFixed(2)} ft`);
      } else {
        toast.info('No relevant elements found within radius');
      }
    } catch (error) {
      toast.error('Error finding elements within radius');
    }
  };

  const checkElementRelationship = async () => {
    if (!viewer || !relationshipTarget) {
      toast.warning('Please select a target element first');
      return;
    }
    
    clearAllVisualizations();
    
    try {
      const result = GeometryTools.getElementRelationship(viewer, elementDbId, relationshipTarget);
      
      if (result) {
        setRelationship(result);
        
        // Select both elements first
        viewer.clearSelection();
        viewer.select([elementDbId, relationshipTarget]);
        
        // Fit view to show both elements with proper camera animation
        viewer.fitToView([elementDbId, relationshipTarget], viewer.model, true);
        
        // Apply highlighting after camera animation
        setTimeout(() => {
          ViewerService.highlightElement(viewer, elementDbId, new THREE.Vector4(0, 0, 1, 0.5)); // Blue for source
          ViewerService.highlightElement(viewer, relationshipTarget, new THREE.Vector4(0, 1, 0, 0.5)); // Green for target
          viewer.impl.invalidate(true, true, true);
        }, 500);
        
        // Visualize both elements
        const box1 = GeometryTools.getElementBoundingBox(viewer, elementDbId);
        const box2 = GeometryTools.getElementBoundingBox(viewer, relationshipTarget);
        
        if (box1 && box2) {
          const wireframe1 = createBoxWireframe(box1, 0x3B82F6);
          const wireframe2 = createBoxWireframe(box2, 0x10B981);
          addOverlay(wireframe1);
          addOverlay(wireframe2);
          
          // Draw line between centers
          const line = createLine(box1.getCenter(new THREE.Vector3()), box2.getCenter(new THREE.Vector3()), 0xFFFFFF);
          addOverlay(line);
        }
        
        // Get element names for better display
        const sourceElementName = await getElementName(elementDbId);
        const targetElementName = await getElementName(relationshipTarget);
        
        const distanceText = result.distance ? ` (${formatDistance(result.distance)})` : '';
        toast.info(`Relationship: ${result.relationship}${distanceText}\nSource: ${sourceElementName}\nTarget: ${targetElementName}`);
      }
    } catch (error) {
      toast.error('Error checking element relationship');
    }
  };

  // Path Analysis Methods
  const setPathPoint = (isStart: boolean) => {
    if (!viewer) return;
    
    const box = GeometryTools.getElementBoundingBox(viewer, elementDbId);
    if (box) {
      const point = box.getCenter(new THREE.Vector3());
      if (isStart) {
        setPathStart(point);
        toast.success('Path start point set');
      } else {
        setPathEnd(point);
        toast.success('Path end point set');
      }
    }
  };

  const analyzePath = () => {
    if (!viewer || !pathStart || !pathEnd) {
      toast.warning('Please set both start and end points');
      return;
    }
    
    clearAllVisualizations();
    const result = GeometryTools.analyzePathClearance(viewer, pathStart, pathEnd, pathWidth, pathHeight);
    
    if (result) {
      setPathAnalysis(result);
      
      // Visualize path
      const line = createLine(pathStart, pathEnd, 0x00FF00);
      addOverlay(line);
      
      // Visualize obstacles
      if (result.obstacles.length > 0) {
        ViewerService.highlightElements(viewer, result.obstacles, new THREE.Vector4(1, 0, 0, 0.5));
      }
      
      const widthInFeet = metersToFeet(result.minWidth);
      toast.info(`Path width: ${widthInFeet.toFixed(2)} ft, Obstacles: ${result.obstacles.length}`);
    }
  };

  // Utility Methods
  const calculateVolume = () => {
    if (!viewer) return;
    
    try {
      const vol = GeometryTools.getElementVolume(viewer, elementDbId);
      setVolume(vol);
      const volInCubicFeet = vol * 35.3147; // Convert cubic meters to cubic feet
      toast.info(`Volume: ${volInCubicFeet.toFixed(2)} ft³ (${vol.toFixed(3)} m³)`);
    } catch (error) {
      toast.error('Error calculating volume');
    }
  };

  const calculateSurfaceArea = () => {
    if (!viewer) return;
    
    try {
      const area = GeometryTools.getElementSurfaceArea(viewer, elementDbId);
      setSurfaceArea(area);
      const areaInSquareFeet = area * 10.7639; // Convert square meters to square feet
      toast.info(`Surface Area: ${areaInSquareFeet.toFixed(2)} ft² (${area.toFixed(3)} m²)`);
    } catch (error) {
      toast.error('Error calculating surface area');
    }
  };

  const checkLevel = () => {
    if (!viewer) return;
    
    try {
      const onLevel = GeometryTools.isElementOnLevel(viewer, elementDbId, levelMin, levelMax);
      setIsOnLevel(onLevel);
      const minInFeet = metersToFeet(levelMin);
      const maxInFeet = metersToFeet(levelMax);
      toast.info(`Element is ${onLevel ? '' : 'NOT '}on level ${minInFeet.toFixed(1)}-${maxInFeet.toFixed(1)} ft`);
    } catch (error) {
      toast.error('Error checking level');
    }
  };

  return (
    <div className="geometry-tools-tester">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 bg-dark-200 rounded-t-lg cursor-pointer hover:bg-dark-300 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <span className="text-lg">🔧</span>
          Geometry Tools Testing
        </h3>
        <span className="text-gray-400 text-lg">
          {isExpanded ? '−' : '+'}
        </span>
      </div>

      {isExpanded && (
        <div className="bg-dark-200 rounded-b-lg border-t border-gray-700">
          {/* Mode Tabs - Fixed at top */}
          <div className="sticky top-0 z-10 bg-dark-200 p-3 pb-0 border-b border-gray-700">
            <div className="flex gap-1 bg-dark-100 p-1 rounded">
            <button
              className={`px-2 py-1 text-xs rounded transition-colors ${
                activeMode === 'distance' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveMode('distance')}
            >
              Distance
            </button>
            <button
              className={`px-2 py-1 text-xs rounded transition-colors ${
                activeMode === 'boundingBox' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveMode('boundingBox')}
            >
              Bounding Box
            </button>
            <button
              className={`px-2 py-1 text-xs rounded transition-colors ${
                activeMode === 'spatial' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveMode('spatial')}
            >
              Spatial
            </button>
            <button
              className={`px-2 py-1 text-xs rounded transition-colors ${
                activeMode === 'path' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveMode('path')}
            >
              Path
            </button>
            <button
              className={`px-2 py-1 text-xs rounded transition-colors ${
                activeMode === 'utilities' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveMode('utilities')}
            >
              Utilities
            </button>
          </div>
          </div>

          {/* Tab Content - Scrollable */}
          <div className="p-3 pt-4 max-h-96 overflow-y-auto custom-scrollbar">
            {/* Distance Measurement Tab */}
            {activeMode === 'distance' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400">Measurement Mode</label>
                <div className="flex gap-2 mt-1">
                  {([
                    { value: 'center-to-center', label: 'Center to Center' },
                    { value: 'surface-to-surface', label: 'Surface to Surface' },
                    { value: 'center-to-surface', label: 'This Center → Target Surface' }
                  ] as Array<{value: DistanceMode; label: string}>).map(mode => (
                    <label key={mode.value} className="flex items-center gap-1 text-xs" title={
                      mode.value === 'center-to-surface' 
                        ? 'From center of this element to surface of target element' 
                        : mode.label
                    }>
                      <input
                        type="radio"
                        checked={distanceMode === mode.value}
                        onChange={() => setDistanceMode(mode.value)}
                        className="text-primary"
                      />
                      <span className="text-gray-300">{mode.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400">Second Element ID</label>
                <div className="flex gap-1 mt-1 mb-2">
                  <button
                    className={`btn btn-small flex-1 ${secondIdType === 'dbId' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setSecondIdType('dbId')}
                  >
                    DbID
                  </button>
                  <button
                    className={`btn btn-small flex-1 ${secondIdType === 'revitId' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setSecondIdType('revitId')}
                  >
                    Revit ID
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type={secondIdType === 'dbId' ? 'number' : 'text'}
                    value={secondDbId}
                    onChange={(e) => setSecondDbId(e.target.value)}
                    placeholder={secondIdType === 'dbId' ? 'Enter DbID' : 'Enter Revit ID'}
                    className="input input-sm flex-1"
                  />
                  <button
                    onClick={startPickingSecond}
                    className={`btn btn-small ${isPickingSecond ? 'btn-warning' : 'btn-secondary'}`}
                  >
                    {isPickingSecond ? 'Picking...' : 'Pick'}
                  </button>
                </div>
              </div>

              <button
                onClick={measureDistance}
                className="btn btn-primary btn-small w-full"
                disabled={!secondDbId}
              >
                Measure Distance
              </button>

              {distanceResult && (
                <div className="space-y-2">
                  <div className="p-2 bg-dark-100 rounded text-xs">
                    <div className="text-green-400 font-semibold mb-1">Distance: {formatDistance(distanceResult.distance)}</div>
                    <div className="text-gray-400">Measurement Type: {distanceResult.type}</div>
                    
                    {/* Explanation of measurement method */}
                    <div className="text-gray-500 text-xs mt-2 italic">
                      {distanceResult.type === 'center-to-center' && '📍 Measuring between element centers'}
                      {distanceResult.type === 'surface-to-surface' && '📐 Measuring closest surfaces (using bounding boxes)'}
                      {distanceResult.type === 'center-to-surface' && (
                        <>
                          📏 Measuring from THIS element's center to TARGET surface
                          <div className="text-xs mt-1">
                            Element {elementDbId} center → Nearest point on element {distanceResult.element2 || secondDbId} bounding box
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="text-gray-500 text-xs mt-1">
                      From: ({distanceResult.point1.x.toFixed(2)}, {distanceResult.point1.y.toFixed(2)}, {distanceResult.point1.z.toFixed(2)})
                    </div>
                    <div className="text-gray-500 text-xs">
                      To: ({distanceResult.point2.x.toFixed(2)}, {distanceResult.point2.y.toFixed(2)}, {distanceResult.point2.z.toFixed(2)})
                    </div>
                  </div>
                  <button
                    onClick={downloadDistanceJSON}
                    className="btn btn-success btn-small w-full"
                  >
                    📥 Download Measurement JSON
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Bounding Box Tab */}
          {activeMode === 'boundingBox' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400">Extension Distance: {metersToFeet(boxExtension).toFixed(1)} ft ({boxExtension.toFixed(1)} m)</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={boxExtension}
                  onChange={(e) => setBoxExtension(Number(e.target.value))}
                  className="w-full mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={showElementBoundingBox}
                  className="btn btn-secondary btn-small"
                >
                  Show Box
                </button>
                <button
                  onClick={showExtendedBoundingBox}
                  className="btn btn-secondary btn-small"
                >
                  Show Extended
                </button>
              </div>

              {/* Family Filter Section */}
              <div className="border border-dark-300 rounded p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-400 font-medium">Family Type Filter</label>
                  <input
                    type="checkbox"
                    checked={useFamilyFilter}
                    onChange={(e) => {
                      setUseFamilyFilter(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedFamilies([]);
                        setAvailableFamilies([]);
                        setFamilySearchTerm('');
                      }
                    }}
                    className="w-4 h-4"
                  />
                </div>
                
                {useFamilyFilter && (
                  <>
                    <div className="text-xs text-gray-500">
                      Keep only elements from selected family types. Run "Find Intersecting" first to discover families.
                    </div>
                    
                    {availableFamilies.length > 0 && (
                      <>
                        {/* Search input */}
                        <input
                          type="text"
                          placeholder="Search families..."
                          value={familySearchTerm}
                          onChange={(e) => setFamilySearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-xs bg-dark-200 border border-dark-400 rounded"
                        />
                        
                        {/* Family list with search filter */}
                        <div className="max-h-32 overflow-y-auto border border-dark-400 rounded p-1">
                          {availableFamilies
                            .filter(family => 
                              family.name.toLowerCase().includes(familySearchTerm.toLowerCase())
                            )
                            .map(family => (
                              <label key={family.name} className="flex items-center p-1 hover:bg-dark-300 cursor-pointer text-xs">
                                <input
                                  type="checkbox"
                                  checked={selectedFamilies.includes(family.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedFamilies([...selectedFamilies, family.name]);
                                    } else {
                                      setSelectedFamilies(selectedFamilies.filter(f => f !== family.name));
                                    }
                                  }}
                                  className="w-3 h-3 mr-2"
                                />
                                <span className="flex-1 truncate">{family.name}</span>
                                <span className="text-gray-500 ml-1">({family.count})</span>
                              </label>
                            ))}
                        </div>
                        
                        {/* Quick actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const filtered = availableFamilies
                                .filter(f => f.name.toLowerCase().includes(familySearchTerm.toLowerCase()))
                                .map(f => f.name);
                              setSelectedFamilies(filtered);
                            }}
                            className="btn btn-secondary btn-small text-xs flex-1"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => setSelectedFamilies([])}
                            className="btn btn-secondary btn-small text-xs flex-1"
                          >
                            Clear All
                          </button>
                        </div>
                      </>
                    )}
                    
                    {selectedFamilies.length > 0 && (
                      <div className="text-xs text-green-400">
                        Selected: {selectedFamilies.length} families
                      </div>
                    )}
                  </>
                )}
              </div>

              <button
                onClick={findIntersectingElements}
                className="btn btn-primary btn-small w-full"
              >
                Find Intersecting Elements
              </button>

              {/* Results Section */}
              {(intersectingElements.length > 0 || excludedElements.length > 0) && (
                <div className="space-y-2">
                  {/* Results Summary */}
                  <div className="p-2 bg-dark-100 rounded text-xs space-y-2">
                    {/* Filter Status */}
                    {useFamilyFilter && selectedFamilies.length > 0 && (
                      <div className="p-2 bg-blue-900/20 border border-blue-700/30 rounded">
                        <div className="text-blue-400 font-semibold mb-1">Family Filter Active</div>
                        <div className="text-gray-400">
                          Keeping only: {selectedFamilies.join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {/* Results Count */}
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-green-400 font-semibold">
                          ✓ {intersectingElements.length} Valid Elements
                        </div>
                        {excludedElements.length > 0 && (
                          <div className="text-yellow-400 text-xs">
                            ✗ {excludedElements.length} Excluded
                          </div>
                        )}
                      </div>
                      <button
                        onClick={visualizeValidElements}
                        className="btn btn-secondary btn-small text-xs px-3 py-1"
                        title="Visualize all valid elements"
                      >
                        👁️ View All
                      </button>
                    </div>
                    
                    {/* Valid Elements List */}
                    {intersectingElements.length > 0 && (
                      <div className="border-t border-dark-300 pt-2">
                        <div className="font-semibold text-gray-300 mb-1">Valid Elements:</div>
                        <div className="max-h-40 overflow-y-auto custom-scrollbar space-y-1">
                          {intersectingElements.map((el, index) => {
                            const familyName = parseFamilyName(el.name);
                            return (
                              <div key={el.dbId} className="p-2 bg-dark-200 rounded hover:bg-dark-300 transition-colors">
                                <div className="flex justify-between items-start gap-2">
                                  <div className="flex-1">
                                    <div className="text-gray-200 font-medium">
                                      {index + 1}. {el.name || `Element_${el.dbId}`}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      Family: {familyName} • ID: {el.dbId}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-xs text-green-400">
                                      {metersToFeet(el.distance).toFixed(2)} ft
                                    </div>
                                    <button
                                      onClick={() => viewIndividualElement(el.dbId, el.name)}
                                      className="btn btn-secondary btn-small text-xs px-2 py-1"
                                      title={`View and highlight ${el.name}`}
                                    >
                                      👁️
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Excluded Elements (Collapsible) */}
                    {excludedElements.length > 0 && (
                      <details className="border-t border-dark-300 pt-2">
                        <summary className="cursor-pointer text-yellow-400 hover:text-yellow-300 text-xs font-semibold">
                          Show Excluded Elements ({excludedElements.length})
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto custom-scrollbar space-y-1">
                          {excludedElements.map((el, idx) => (
                            <div key={idx} className="p-1 bg-dark-200/50 rounded text-xs">
                              <div className="text-gray-400">{el.name}</div>
                              <div className="text-gray-500 text-xs">
                                Reason: {el.reason}
                                {el.distance !== undefined && ` • ${metersToFeet(el.distance).toFixed(2)} ft`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={visualizeValidElements}
                      className="btn btn-primary btn-small flex-1"
                    >
                      👁️ Visualize
                    </button>
                    <button
                      onClick={downloadIntersectionsJSON}
                      className="btn btn-success btn-small flex-1"
                    >
                      📥 Export JSON
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Spatial Queries Tab */}
          {activeMode === 'spatial' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400">Search Radius: {metersToFeet(searchRadius).toFixed(1)} ft ({searchRadius.toFixed(1)} m)</label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(Number(e.target.value))}
                  className="w-full mt-1"
                />
              </div>

              {/* Family Filter for Spatial Queries */}
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={useSpatialFamilyFilter}
                    onChange={(e) => setUseSpatialFamilyFilter(e.target.checked)}
                    className="text-primary"
                  />
                  <span className="text-xs text-gray-400">Filter by Families</span>
                </label>
                
                {useSpatialFamilyFilter && (
                  <div className="space-y-2 p-2 bg-dark-100 rounded">
                    {availableFamilies.length === 0 ? (
                      <button
                        onClick={loadAvailableFamilies}
                        disabled={isLoadingFamilies}
                        className="btn btn-secondary btn-small w-full"
                      >
                        {isLoadingFamilies ? 'Loading...' : 'Load Available Families'}
                      </button>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder="Search families..."
                          value={familySearchTerm}
                          onChange={(e) => setFamilySearchTerm(e.target.value)}
                          className="w-full px-2 py-1 bg-dark-200 text-white text-xs rounded"
                        />
                        <div className="max-h-32 overflow-y-auto custom-scrollbar space-y-1">
                          {availableFamilies
                            .filter(f => f.name.toLowerCase().includes(familySearchTerm.toLowerCase()))
                            .map(family => (
                              <label key={family.name} className="flex items-center gap-2 text-xs hover:bg-dark-200 p-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={selectedSpatialFamilies.includes(family.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedSpatialFamilies([...selectedSpatialFamilies, family.name]);
                                    } else {
                                      setSelectedSpatialFamilies(selectedSpatialFamilies.filter(f => f !== family.name));
                                    }
                                  }}
                                  className="text-primary"
                                />
                                <span className="flex-1 text-gray-300">{family.name}</span>
                                <span className="text-gray-500">({family.count})</span>
                              </label>
                            ))}
                        </div>
                        {selectedSpatialFamilies.length > 0 && (
                          <div className="text-xs text-blue-400">
                            Selected: {selectedSpatialFamilies.length} families
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={findElementsWithinRadius}
                className="btn btn-primary btn-small w-full"
              >
                Find Elements Within Radius
              </button>

              {elementsInRadius.length > 0 && (
                <div className="p-2 bg-dark-100 rounded text-xs">
                  <div className="text-green-400 mb-2">Found {elementsInRadius.length} elements (center-to-surface)</div>
                  <div className="max-h-32 overflow-y-auto custom-scrollbar space-y-1">
                    {elementsInRadius.map((el: any, index: number) => (
                      <div key={el.dbId} className="p-1 bg-dark-200 rounded hover:bg-dark-300 transition-colors">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <div className="text-gray-200 font-medium">
                              {index + 1}. {el.name || `Element_${el.dbId}`}
                            </div>
                            <div className="text-xs text-gray-400">
                              ID: {el.dbId}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-green-400">
                              {metersToFeet(el.distance).toFixed(2)} ft
                            </div>
                            <button
                              onClick={() => viewIndividualElement(el.dbId, el.name)}
                              className="btn btn-secondary btn-small text-xs px-2 py-1"
                              title={`View and highlight ${el.name}`}
                            >
                              👁️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-gray-400">Check Relationship With</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    value={relationshipTarget || ''}
                    onChange={(e) => setRelationshipTarget(Number(e.target.value) || null)}
                    placeholder="Target DbID"
                    className="input input-sm flex-1"
                  />
                  <button
                    onClick={checkElementRelationship}
                    className="btn btn-secondary btn-small"
                    disabled={!relationshipTarget}
                  >
                    Check
                  </button>
                </div>
              </div>

              {relationship && (
                <div className="p-2 bg-dark-100 rounded text-xs">
                  <div className="text-blue-400 font-semibold mb-1">Relationship: {relationship.relationship}</div>
                  {relationship.distance !== undefined && (
                    <div className="text-gray-400">
                      Distance: {metersToFeet(relationship.distance).toFixed(2)} ft
                      <span className="text-gray-500"> ({relationship.distance.toFixed(3)} m)</span>
                    </div>
                  )}
                  <div className="text-gray-500 text-xs mt-1">
                    {relationship.relationship === 'intersecting' && '⚠️ Elements are overlapping'}
                    {relationship.relationship === 'adjacent' && '✓ Elements are touching'}
                    {relationship.relationship === 'separated' && '↔️ Elements are apart'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Path Analysis Tab */}
          {activeMode === 'path' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setPathPoint(true)}
                  className={`btn btn-small ${pathStart ? 'btn-success' : 'btn-secondary'}`}
                >
                  Set Start
                </button>
                <button
                  onClick={() => setPathPoint(false)}
                  className={`btn btn-small ${pathEnd ? 'btn-success' : 'btn-secondary'}`}
                >
                  Set End
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-400">Width: {metersToFeet(pathWidth).toFixed(1)} ft ({pathWidth.toFixed(1)} m)</label>
                  <input
                    type="range"
                    min="0.5"
                    max="5"
                    step="0.1"
                    value={pathWidth}
                    onChange={(e) => setPathWidth(Number(e.target.value))}
                    className="w-full mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400">Height: {metersToFeet(pathHeight).toFixed(1)} ft ({pathHeight.toFixed(1)} m)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.1"
                    value={pathHeight}
                    onChange={(e) => setPathHeight(Number(e.target.value))}
                    className="w-full mt-1"
                  />
                </div>
              </div>

              <button
                onClick={analyzePath}
                className="btn btn-primary btn-small w-full"
                disabled={!pathStart || !pathEnd}
              >
                Analyze Path
              </button>

              {pathAnalysis && (
                <div className="p-2 bg-dark-100 rounded text-xs">
                  <div className="text-yellow-400">Min Width: {metersToFeet(pathAnalysis.minWidth).toFixed(2)} ft</div>
                  <div className="text-red-400">Obstacles: {pathAnalysis.obstacles.length}</div>
                </div>
              )}
            </div>
          )}

          {/* Utilities Tab */}
          {activeMode === 'utilities' && (
            <div className="space-y-3">
              <button
                onClick={calculateVolume}
                className="btn btn-secondary btn-small w-full"
              >
                Calculate Volume
              </button>

              {volume !== null && (
                <div className="p-2 bg-dark-100 rounded text-xs">
                  <div className="text-blue-400">Volume: {(volume * 35.3147).toFixed(2)} ft³ ({volume.toFixed(3)} m³)</div>
                </div>
              )}

              <button
                onClick={calculateSurfaceArea}
                className="btn btn-secondary btn-small w-full"
              >
                Calculate Surface Area
              </button>

              {surfaceArea !== null && (
                <div className="p-2 bg-dark-100 rounded text-xs">
                  <div className="text-green-400">Surface Area: {(surfaceArea * 10.7639).toFixed(2)} ft² ({surfaceArea.toFixed(3)} m²)</div>
                </div>
              )}

              <div>
                <label className="text-xs text-gray-400">Check Level Range (in feet)</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <input
                    type="number"
                    value={metersToFeet(levelMin).toFixed(1)}
                    onChange={(e) => setLevelMin(Number(e.target.value) / 3.28084)}
                    placeholder="Min (ft)"
                    className="input input-sm"
                  />
                  <input
                    type="number"
                    value={metersToFeet(levelMax).toFixed(1)}
                    onChange={(e) => setLevelMax(Number(e.target.value) / 3.28084)}
                    placeholder="Max (ft)"
                    className="input input-sm"
                  />
                </div>
              </div>

              <button
                onClick={checkLevel}
                className="btn btn-secondary btn-small w-full"
              >
                Check If On Level
              </button>

              {isOnLevel !== null && (
                <div className="p-2 bg-dark-100 rounded text-xs">
                  <div className={isOnLevel ? 'text-green-400' : 'text-red-400'}>
                    Element is {isOnLevel ? '' : 'NOT '}on level {metersToFeet(levelMin).toFixed(1)}-{metersToFeet(levelMax).toFixed(1)} ft
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clear Visualizations Button */}
            <div className="mt-3 pt-3 border-t border-gray-700">
              <button
                onClick={clearAllVisualizations}
                className="btn btn-secondary btn-small w-full"
              >
                Clear All Visualizations
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}